(ns app.front.view.group.reporting.cases
  (:require [app.domain.execution.model :as execution-model]
            [app.front.ui.filter-common :as filter-common]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.reporting.common :as reporting-common]
            [app.front.view.group.reporting.download-button :as rdb]
            [app.front.view.group.reporting.filters :as reporting-filters]
            [app.front.view.group.reporting.form-fields :as reporting-ff]
            [app.front.view.group.reporting.property-attributes :as reporting-pa]
            [app.query.filter-util :as filter-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

(defn optional-fields []
  (cond-> [:reporting/case-description-of-location
           :reporting/case-in-proximity
           :reporting/case-non-owner-violation]
    (ff/enabled? :feature-flag.id/property-db-units)
    (conj :reporting/application-unit-type-and-number)))

(def default-settings
  {:categories           #{}
   :states               #{}
   :date-selector-mode   #{:reporting.date-range-options/current-month}
   :selected-form-fields nil})

(rf/reg-event-fx ::select-saved-filter
  (fn [{:keys [db]} [_ selected-filter]]
    (let [filter-values      (filter-util/filter-entity->search-values-map selected-filter)
          date-selector-mode (->> filter-values :date-selector-mode first)]
      {:fx [[:dispatch [::reporting-common/set-selected-categories (:categories filter-values)]]
            [:dispatch [::reporting-common/set-selected-states (:states filter-values)]]
            [:dispatch [::reporting-common/set-category-missing-selected (first (:include-category-missing filter-values))]]]
       :db (-> db
               (p/assoc-if ::reporting-common/date-selector-mode date-selector-mode
                           ::reporting-ff/selected (->> (:form-fields filter-values)
                                                        (map-indexed vector)
                                                        (into {}))))})))

(rf/reg-event-db ::initialize-settings
  (fn [db _]
    (let [{:keys [categories states date-selector-mode selected-form-fields]} default-settings]
      (-> db
          (assoc ::reporting-common/selected-states states)
          (assoc ::reporting-common/date-selector-mode (first date-selector-mode)
                 ::reporting-common/selected-categories categories
                 ::reporting-common/category-missing-selected? false
                 ::reporting-ff/selected selected-form-fields)))))

(rf/reg-sub ::filter-settings
  :<- [::gas/group]
  :<- [::reporting-common/selected-categories]
  :<- [::reporting-common/date-selector-mode]
  :<- [::reporting-common/selected-states]
  :<- [::reporting-common/category-missing-selected?]
  :<- [::reporting-ff/rows]
  (fn [[group
        categories
        date-selector-mode
        states
        category-missing-selected?
        form-fields] _]
    (merge (util/mmap {:date-selector-mode #{date-selector-mode}
                       :group-id           #{(:db/id group)}
                       :form-fields        (->> form-fields (sort-by first) (mapv second))}
                      categories
                      states)
           (when category-missing-selected?
             {:include-category-missing #{category-missing-selected?}}))))

(rf/reg-sub ::filters-in-default-state?
  :<- [::filter-settings]
  (fn [filter-settings]
    (= (dissoc filter-settings :group-id) default-settings)))

(rf/reg-sub ::conditions
  :<- [::gas/group-id]
  :<- [::filter-settings]
  (fn [[group-id filter-settings]]
    (-> filter-settings
        (assoc :group-id #{group-id})
        (filter-common/filter-values->conditions #{:form-fields}))))

(rf/reg-sub ::query
  :<- [::gas/group-id]
  :<- [::reporting-common/selected-categories]
  :<- [::reporting-common/category-missing-selected?]
  :<- [::reporting-common/selected-states]
  :<- [::reporting-common/date-range]
  :<- [::reporting-common/selected-optional-fields]
  :<- [::reporting-ff/rows]
  :<- [::reporting-common/selected-party-roles]
  :<- [::reporting-pa/selected-attributes]
  (fn [[group-id categories category-missing-selected? states
        [start-date end-date] optional-fields form-fields party-roles selected-attributes] _]
    (-> {:group-id group-id}
        (p/assoc-if :categories (seq categories)
                    :include-category-missing category-missing-selected?
                    :states (seq states)
                    :start-date start-date
                    :end-date end-date
                    :optional-fields optional-fields
                    :form-fields (->> form-fields (sort-by first) (mapv second))
                    :party-roles (seq party-roles)
                    :property-attribute-templates (when (seq selected-attributes) selected-attributes)))))

(defn view [match]
  (let [tr     (<sub [::gas/group-tr])
        states (if (= (<sub [::gas/group-primary-permit-type])
                      :permit-type/PWO)
                 execution-model/work-order-states
                 execution-model/case-states)
        query  (<sub [::query])]
    [reporting-common/report-page
     match
     tr
     [:section
      [reporting-filters/filters tr {:filter-type             :filter.type/reporting.cases
                                     :filter-settings-sub     ::filter-settings
                                     :default-filters-sub     ::filters-in-default-state?
                                     :conditions-sub          ::conditions
                                     :select-saved-filter-evt ::select-saved-filter
                                     :initialize-settings-evt ::initialize-settings}]]
     [:section
      [:h3.m-0 (tr [:reporting/time-range])]

      [:div.w-full
       [reporting-common/date-range-selector tr "case-date-range" reporting-common/date-range-options]]]

     [:section
      [:div.w-full.mb-6
       [reporting-common/category-selector tr true]]
      [:div.w-full.mb-6
       [reporting-common/status-selector {:tr tr :states states :header-key :reporting.cases/status}]]
      [:div.w-full.mb-8
       [reporting-common/optional-field-selector tr :cases (optional-fields)]]
      [:div.w-full.mb-8
       [reporting-common/related-parties-selector tr]]
      [:div.w-full-mb-8
       [reporting-ff/form-field-selector tr false]]
      [:div.w-full-mb-8
       [reporting-pa/property-attribute-selector tr]]]

     [:div.w-full.mb-8
      [rdb/download-button
       (util/mmap {:base-url "/api/command/report/download-case-report"}
                  tr query)]]]))

(def route
  {:name        :view/group.reporting.cases
   :view        view
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [params]
                                (let [group-id (-> params :path :group-id)]
                                  (>evt [::reporting-filters/fetch-saved-filters group-id])
                                  (>evt [::reporting-common/init group-id (first reporting-common/date-range-options)])))
                  :stop       (fn []
                                (>evt [::initialize-settings]))}]})
